import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Save, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface TemplateData {
  id: string;
  title: string;
  description: string;
  category: string;
  affirmations: string[];
  sample_images: string[];
  representative_image: string;
}

export default function ManifestationTemplatesManager() {
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('manifestation_templates')
        .select('*')
        .order('title');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive"
      });
    }
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('manifestation_templates')
        .upsert({
          id: selectedTemplate.id,
          title: selectedTemplate.title,
          description: selectedTemplate.description,
          category: selectedTemplate.category,
          affirmations: selectedTemplate.affirmations,
          sample_images: selectedTemplate.sample_images,
          representative_image: selectedTemplate.representative_image
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template saved successfully"
      });
      
      loadTemplates();
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: "Failed to save template",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('manifestation_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template deleted successfully"
      });
      
      loadTemplates();
      if (selectedTemplate?.id === id) setSelectedTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive"
      });
    }
  };

  const createNewTemplate = () => {
    setSelectedTemplate({
      id: crypto.randomUUID(),
      title: 'New Template',
      description: '',
      category: 'Lifestyle',
      affirmations: [''],
      sample_images: [''],
      representative_image: ''
    });
  };

  const updateAffirmation = (index: number, value: string) => {
    if (!selectedTemplate) return;
    const newAffirmations = [...selectedTemplate.affirmations];
    newAffirmations[index] = value;
    setSelectedTemplate({ ...selectedTemplate, affirmations: newAffirmations });
  };

  const addAffirmation = () => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      affirmations: [...selectedTemplate.affirmations, '']
    });
  };

  const removeAffirmation = (index: number) => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      affirmations: selectedTemplate.affirmations.filter((_, i) => i !== index)
    });
  };

  const updateImage = (index: number, value: string) => {
    if (!selectedTemplate) return;
    const newImages = [...selectedTemplate.sample_images];
    newImages[index] = value;
    setSelectedTemplate({ ...selectedTemplate, sample_images: newImages });
  };

  const addImage = () => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      sample_images: [...selectedTemplate.sample_images, '']
    });
  };

  const removeImage = (index: number) => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      sample_images: selectedTemplate.sample_images.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Manifestation Templates</h2>
          <p className="text-muted-foreground">Manage template affirmations and images</p>
        </div>
        <Button onClick={createNewTemplate}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Template List */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Templates</h3>
          <div className="space-y-2">
            {templates.map(template => (
              <div
                key={template.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent cursor-pointer"
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="flex items-center gap-3">
                  {template.representative_image && (
                    <img
                      src={template.representative_image}
                      alt={template.title}
                      className="w-10 h-10 rounded object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium">{template.title}</p>
                    <p className="text-sm text-muted-foreground">{template.category}</p>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTemplate(template.id);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>

        {/* Template Editor */}
        {selectedTemplate && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Edit Template</h3>
              <Button onClick={saveTemplate} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              <div>
                <Label>Title</Label>
                <Input
                  value={selectedTemplate.title}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={selectedTemplate.description}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, description: e.target.value })}
                />
              </div>

              <div>
                <Label>Category</Label>
                <Input
                  value={selectedTemplate.category}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, category: e.target.value })}
                />
              </div>

              <div>
                <Label>Representative Image URL</Label>
                <Input
                  value={selectedTemplate.representative_image}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, representative_image: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Affirmations</Label>
                  <Button size="sm" variant="outline" onClick={addAffirmation}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {selectedTemplate.affirmations.map((affirmation, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={affirmation}
                        onChange={(e) => updateAffirmation(index, e.target.value)}
                        placeholder="Enter affirmation..."
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeAffirmation(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Sample Images (URLs)</Label>
                  <Button size="sm" variant="outline" onClick={addImage}>
                    <ImageIcon className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {selectedTemplate.sample_images.map((image, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={image}
                        onChange={(e) => updateImage(index, e.target.value)}
                        placeholder="https://images.unsplash.com/..."
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeImage(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
